import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { trackAdEvent } from '../services/api';
import telegramService from '../services/telegram';
import BottomSheet from './BottomSheet';
import CustomBadge from './CustomBadge';
import MarkdownLink from './MarkdownLink';

const AdCard = ({ ad, source = null }) => {
  const navigate = useNavigate();
  const [showLabelModal, setShowLabelModal] = useState(false);
  const hasTrackedView = useRef(false);

  // Track view on mount
  useEffect(() => {
    if (ad?.id && !hasTrackedView.current) {
      trackAdEvent(ad.id, 'view', source);
      hasTrackedView.current = true;
    }
  }, [ad?.id, source]);

  const getImageUrl = (image) => {
    if (!image) return null;

    // Если это объект с url
    if (image.url) {
      return image.url.startsWith('http') ? image.url : image.url;
    }

    // Если это строка
    if (typeof image === 'string') {
      return image.startsWith('http') ? image : image;
    }

    return null;
  };

  const handleClick = () => {
    if (ad.link) {
      trackAdEvent(ad.id, 'click', source);

      // Если ссылка внешняя - открываем в браузере
      if (ad.link.startsWith('http://') || ad.link.startsWith('https://')) {
        telegramService.openLink(ad.link);
      } else {
        // Внутренняя навигация
        navigate(ad.link);
      }
    }
  };

  const handleLabelClick = (e) => {
    e.stopPropagation();
    trackAdEvent(ad.id, 'label_click');
    setShowLabelModal(true);
  };

  // Стабильная ссылка: BottomSheet регистрирует onClose в стеке модалок
  // эффектом по этой зависимости — новая функция на каждый рендер
  // перерегистрировала бы панель без причины
  const closeLabelModal = useCallback(() => setShowLabelModal(false), []);

  const imageUrl = getImageUrl(ad.image);

  return (
    <>
      <div
        className="relative rounded-xl overflow-hidden bg-theme-card border border-theme-border cursor-pointer w-full"
        style={{ height: '300px' }}
        onClick={handleClick}
      >
        {/* Изображение */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt={ad.title}
            className="w-full h-full object-cover"
          />
        )}

        {/* Затемнение сверху для читаемости текста */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />

        {/* Описание и название в левом верхнем углу.
            `right-3` обязателен: без правой границы блок тянулся по
            содержимому до самого края карточки, и место переноса строки
            зависело от случайных пикселей ширины колонки — одно и то же
            название рвалось по-разному на главной, категориях и «Рядом» */}
        <div className="absolute top-3 left-3 right-3 z-10">
          {ad.description && (
            <p className="text-white/90 text-caption leading-tight line-clamp-2">
              {ad.description}
            </p>
          )}
          {/* Кегль как у карточки места: body 16px, вес 600 */}
          <h3 className="text-white text-body font-semibold leading-tight mt-1">
            {ad.title}
          </h3>
        </div>

        {/* Метка рекламы в левом нижнем углу — тот же `CustomBadge`, что
            у бейджа и расстояния на карточке места. Своя инлайн-копия
            разошлась с ним по кеглю, высоте и полям */}
        {ad.showCustomLabel && ad.customLabel && (
          <div className="absolute bottom-3 left-3">
            <CustomBadge
              label={ad.customLabel}
              onClick={ad.labelContent ? handleLabelClick : undefined}
              ariaLabel={ad.labelContent ? 'Информация о рекламе' : undefined}
            />
          </div>
        )}
      </div>

      {/* Панель с пояснением метки. Единственная модалка проекта, которую
          Этап 3 пропустил: тот прогон искал по `borderRadius: '24px'`,
          а здесь стоял `rounded-t-2xl` классом. Отсюда и всё остальное —
          «Назад» уводила со страницы вместо закрытия панели, скролл фона
          не блокировался, Escape и ловушки фокуса не было */}
      <BottomSheet
        isOpen={showLabelModal && Boolean(ad.labelContent)}
        onClose={closeLabelModal}
        title={ad.customLabel}
        footer={
          <button
            type="button"
            onClick={closeLabelModal}
            className="h-12 w-full rounded-md bg-action text-label text-on-action transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Закрыть
          </button>
        }
      >
        {/* Классы `prose prose-sm` были мёртвыми: плагина
            @tailwindcss/typography в проекте нет — размеры задаются явно */}
        <div className="text-body text-theme-text-primary">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
              ul: ({ node, ...props }) => (
                <ul className="mb-3 list-inside list-disc last:mb-0" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="mb-3 list-inside list-decimal last:mb-0" {...props} />
              ),
              li: ({ node, ...props }) => <li className="mb-1" {...props} />,
              // Ссылка на наш домен открывается внутри приложения, внешняя —
              // через Telegram. Раньше клик не обрабатывался вовсе: любая
              // ссылка уводила человека из мини-приложения в браузер
              a: MarkdownLink,
            }}
          >
            {ad.labelContent}
          </ReactMarkdown>
        </div>
      </BottomSheet>
    </>
  );
};

export default AdCard;
